import React from 'react';

const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);

/**
 * Source colours. Web keeps the page's green; the app gets a distinct blue so
 * the split between them reads without a legend.
 */
const SOURCE_STYLE = {
  web: { bar: 'bg-[#1c5f4c]', dot: 'bg-[#1c5f4c]' },
  app: { bar: 'bg-sky-700', dot: 'bg-sky-700' },
  unknown: { bar: 'bg-gray-400', dot: 'bg-gray-400' }
};

function Tile({ label, value, sub }) {
  return (
    <div className="flex-1 min-w-[160px] rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">{value}</div>
      {sub ? <div className="mt-0.5 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

/**
 * Sign-ins and device types for the people in view.
 *
 * Read from sign-in records rather than activity, so it reaches back further
 * than time tracking does. "Device type" is the honest unit here: two identical
 * laptops cannot be told apart, but a laptop, a phone browser and the app can.
 */
const DeviceBreakdown = ({ data, isLoading, isError }) => {
  if (isLoading) {
    return (
      <section className="mt-6 rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-gray-500">
        Loading sign-ins…
      </section>
    );
  }
  if (isError) {
    return (
      <section className="mt-6 rounded-lg border border-gray-200 bg-white py-8 text-center text-sm text-red-600">
        Could not load sign-ins. Try again in a moment.
      </section>
    );
  }
  if (!data) return null;

  const types = data.device_types || [];
  const maxUsers = types.reduce((m, t) => Math.max(m, t.users || 0), 0);
  const web = data.sources?.web || { users: 0, logins: 0 };
  const app = data.sources?.app || { users: 0, logins: 0 };
  const automation = data.automation || { logins: 0, users: 0, clients: [] };

  return (
    <section className="mt-6">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-gray-900">Sign-ins and devices</h2>
        <span className="text-xs text-gray-500">
          {data.roster_users} {plural(data.roster_users, 'person', 'people')} in scope
        </span>
      </div>

      <div className="flex flex-wrap gap-3">
        <Tile
          label="Logins"
          value={data.logins}
          sub={`by ${data.users_with_logins} ${plural(data.users_with_logins, 'person', 'people')}`}
        />
        <Tile
          label="On more than one device"
          value={`${data.multi_device_users} / ${data.users_seen}`}
          sub="people seen on 2+ device types"
        />
        <Tile
          label="Web"
          value={web.users}
          sub={`${plural(web.users, 'person', 'people')} · ${web.logins} ${plural(web.logins, 'login')}`}
        />
        <Tile
          label="Mobile app"
          value={app.users}
          sub={`${plural(app.users, 'person', 'people')} · ${app.logins} ${plural(app.logins, 'login')}`}
        />
      </div>

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2.5 text-left font-medium">Device type</th>
              <th className="px-4 py-2.5 text-right font-medium">People</th>
              <th className="px-4 py-2.5 text-left font-medium" />
              <th className="px-4 py-2.5 text-right font-medium">Logins</th>
            </tr>
          </thead>
          <tbody>
            {!types.length ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  No sign-ins in this period.
                </td>
              </tr>
            ) : (
              types.map((type) => {
                const style = SOURCE_STYLE[type.source] || SOURCE_STYLE.unknown;
                const width =
                  maxUsers > 0 && type.users > 0
                    ? Math.max(3, (type.users / maxUsers) * 100)
                    : 0;
                return (
                  <tr key={type.key} className="border-t border-gray-100">
                    <td className="px-4 py-2.5 text-gray-900">
                      <span className={`mr-2 inline-block h-2 w-2 rounded-full ${style.dot}`} />
                      {type.label}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-900">
                      {type.users}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="block h-2 w-full max-w-[220px] rounded-full bg-gray-100">
                        <span
                          className={`block h-2 rounded-full ${style.bar}`}
                          style={{ width: `${width}%` }}
                        />
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                      {type.logins}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {automation.users > 0 ? (
        <p className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Not counted: {automation.logins} {plural(automation.logins, 'sign-in')} by{' '}
          {automation.clients.join(', ')} using {automation.users} staff{' '}
          {plural(automation.users, 'account')}. These are scripts and API tools, not
          people, so they are left out of every figure above.
        </p>
      ) : null}

      <p className="mt-2 max-w-3xl text-xs leading-relaxed text-gray-500">
        A login is a sign-in that started in this period. A device type counts when a
        signed-in session was in use during it — most people never sign out, so older
        sign-ins still count while they are being used. Two identical setups, such as
        two Windows laptops on Chrome, count as one device type. Sign-in records go
        back further than time tracking, so older periods can show logins with no
        time recorded.
      </p>
    </section>
  );
};

export default DeviceBreakdown;
